import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleHealthyEatsVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "healthy-eats-vancouvers-freshest-salad-bars";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Field & Social",
    "section-2": "Tractor Everyday Healthy Foods",
    "section-3": "Railtown Café",
    "section-4": "Green Leaf Salad Bar",
    "section-5": "Salad Loop",
    "section-6": "Hungry Guys Kitchen",
    "section-7": "Peqish",
    "section-8": "The Chopped Leaf",
    "local-regulations": "Local Regulations / Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com",
      label: "Destination Vancouver – Healthy Dining",
    },
    {
      url: "https://www.vch.ca/your-environment/food-safety",
      label: "Vancouver Coastal Health – Food Safety",
    },
    {
      url: "https://vancouver.ca/green-vancouver/single-use-items.aspx",
      label: "City of Vancouver – Single-Use Item Reduction Strategy",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each location's id correlates to the respective h2 in the code, plus an anchor paragraph.
  // Ensure lat/lng are to 5 decimals.
  const locations = [
    {
      id: "section-1",
      lat: 49.28201,
      lng: -123.11375,
      popupText: "Field & Social",
    },
    {
      id: "section-2",
      lat: 49.28800,
      lng: -123.11292,
      popupText: "Tractor Everyday Healthy Foods",
    },
    {
      id: "section-3",
      lat: 49.27980,
      lng: -123.12000,
      popupText: "Railtown Café",
    },
    {
      id: "section-4",
      lat: 49.28670,
      lng: -123.12700,
      popupText: "Green Leaf Salad Bar",
    },
    {
      id: "section-5",
      lat: 49.28460,
      lng: -123.12200,
      popupText: "Salad Loop",
    },
    {
      id: "section-6",
      lat: 49.27930,
      lng: -123.12150,
      popupText: "Hungry Guys Kitchen",
    },
    {
      id: "section-7",
      lat: 49.28380,
      lng: -123.11470,
      popupText: "Peqish",
    },
    {
      id: "section-8",
      lat: 49.28000,
      lng: -123.11700,
      popupText: "The Chopped Leaf",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Healthy Eats: Vancouver’s Freshest Salad Bars</title>
        <meta
          name="description"
          content="Explore Vancouver’s top salad bars—Field & Social, Tractor, Railtown Café, and more—each offering fresh, delicious, and healthy menu options. Learn about local regulations, FAQ, and key locations."
        />
        <meta
          name="keywords"
          content="Vancouver, salad bars, healthy eating, fresh ingredients, local regulations"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/healthy-eats-vancouvers-freshest-salad-bars.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Healthy Eats: Vancouver’s Freshest Salad Bars"
        />
        <meta
          property="og:description"
          content="A comprehensive guide to Vancouver's best salad bars, including addresses, costs, regulations, and tips."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/healthy-eats-vancouvers-freshest-salad-bars.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/healthy-eats-vancouvers-freshest-salad-bars.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Healthy Eats: Vancouver’s Freshest Salad Bars"
        />
        <meta
          name="twitter:description"
          content="A comprehensive guide to Vancouver's best salad bars, including addresses, costs, and local regulations."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/healthy-eats-vancouvers-freshest-salad-bars.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="A colorful array of fresh salad ingredients"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is celebrated for its vibrant culinary scene, where
              health and sustainability take center stage. Salad bars in this
              city are more than just greens—they’re hubs of creativity that
              reflect Vancouver’s farm-to-table ethos and diverse taste
              preferences. If you’re looking for fresh, flavorful, and nourishing
              meals, you’ve come to the right place. In this article, we explore
              eight of Vancouver’s best salad bars, each with unique features
              such as organic produce, superfood ingredients, or locally sourced
              offerings. Whether you’re vegan, vegetarian, or an omnivore aiming
              to eat cleaner, these salad spots offer countless customization
              options. Let’s dive into the details, including addresses,
              operating hours, approximate costs, and more.
            </p>

            {/* SECTION: Field & Social */}
            <h2 id="section-1">Field & Social</h2>
            <p>
              A popular downtown choice for office workers and health-conscious
              diners, Field &amp; Social elevates the classic salad with locally
              sourced ingredients and a rotating seasonal menu. The concept
              centers on carefully crafted recipes—like their Rustic Orzo Bowl or
              other special combinations—topped with house-made dressings. Their
              sleek, modern interior and locally brewed kombucha on tap add to
              the trendiness. Expect to pay around $12–$16 CAD for a hearty
              salad that’s sure to leave you energized.
            </p>
            <p
              ref={(el) => setSpotRef("section-1", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.28201}
                coordX={-123.11375}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;415 Dunsmuir Street, Vancouver, BC
            </p>

            {/* SECTION: Tractor Everyday Healthy Foods */}
            <h2 id="section-2">Tractor Everyday Healthy Foods</h2>
            <p>
              With multiple locations across Vancouver, Tractor is known for its
              bright, inviting atmosphere and extensive lineup of salads,
              soups, and protein bowls. Whether you’re picking up a Miso Tofu
              Salad or mixing and matching their roasted veggies and grains,
              you’ll appreciate the fresh ingredients and balanced flavors.
              Prices range from about $11–$15 CAD, with vegan, vegetarian, and
              gluten-free options clearly labeled.
            </p>
            <p
              ref={(el) => setSpotRef("section-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.28800}
                coordX={-123.11292}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;335 Burrard Street, Vancouver, BC
            </p>

            {/* SECTION: Railtown Café */}
            <h2 id="section-3">Railtown Café</h2>
            <p>
              Famous for its massive, build-your-own salads, Railtown Café is a
              local favorite with a cult following. Start by choosing a base
              (greens or grains), then add proteins, veggies, and a dressing.
              Portions are generous, with salads typically priced around $14–$18
              CAD. If you’re after a heartier option, try their “City’s Best
              Salad” with roast chicken, ancient grains, lemon dressing, and a
              variety of fresh vegetables.
            </p>
            <p
              ref={(el) => setSpotRef("section-3", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.27980}
                coordX={-123.12000}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;968 Howe Street, Vancouver, BC
            </p>

            {/* SECTION: Green Leaf Salad Bar */}
            <h2 id="section-4">Green Leaf Salad Bar</h2>
            <p>
              Tucked away in Coal Harbour, Green Leaf Salad Bar merges a modern
              salad concept with authentic Mediterranean flavors. Build your own
              salad with toppings like chickpeas, hummus, and falafel, or opt
              for a Lebanese-inspired plate. Most items cost under $10 CAD,
              making this a budget-friendly gem that doesn’t compromise on
              quality or taste.
            </p>
            <p
              ref={(el) => setSpotRef("section-4", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.28670}
                coordX={-123.12700}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;560 Bute Street, Vancouver, BC
            </p>

            {/* SECTION: Salad Loop */}
            <h2 id="section-5">Salad Loop</h2>
            <p>
              If variety is your priority, Salad Loop offers a self-serve bar
              with over 100 ingredients. From fresh greens and roasted veggies
              to cheeses, fruits, and legumes, it’s a DIY paradise. You pay by
              weight, generally spending about $10–$15 CAD for a loaded
              container. Salad Loop operates in various downtown food court
              locations—perfect for a quick, healthy lunch.
            </p>
            <p
              ref={(el) => setSpotRef("section-5", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.28460}
                coordX={-123.12200}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;1055 West Georgia Street (Royal Centre Mall), Vancouver, BC
            </p>

            {/* SECTION: Hungry Guys Kitchen */}
            <h2 id="section-6">Hungry Guys Kitchen</h2>
            <p>
              At Hungry Guys Kitchen, salads are as customizable as their
              burritos and wraps. Build your own bowl or order a signature
              Avocado Taco Salad for around $10–$13 CAD. A casual atmosphere and
              house-made dressings (like chipotle lime) keep patrons coming
              back. This Granville Street spot is also open late on weekends,
              making healthy eating accessible after hours.
            </p>
            <p
              ref={(el) => setSpotRef("section-6", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.27930}
                coordX={-123.12150}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;988 Granville Street, Vancouver, BC
            </p>

            {/* SECTION: Peqish */}
            <h2 id="section-7">Peqish</h2>
            <p>
              For grab-and-go gourmet, Peqish promises 100% in-house, preservative-free
              salads and bowls. From a smoked salmon salad to a simple garden
              mix, the menu offers something for every palate. Prices hover
              around $9–$12 CAD, and they’re open Monday to Friday for breakfast
              and lunch. Though seating is limited, the speed and flavor make it
              a top choice for those short on time.
            </p>
            <p
              ref={(el) => setSpotRef("section-7", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.28380}
                coordX={-123.11470}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;552 Seymour Street, Vancouver, BC
            </p>

            {/* SECTION: The Chopped Leaf */}
            <h2 id="section-8">The Chopped Leaf</h2>
            <p>
              A well-known Canadian chain, The Chopped Leaf stands out for its
              extreme level of customization and transparency. Choose from their
              menu of signature salads or craft your own, selecting every
              component from greens to proteins and dressings. Prices range from
              $11–$14 CAD, and their “No. 5 Salad,” packed with avocado, candied
              pecans, and feta, is a popular pick. If you need a quick, reliable
              salad, The Chopped Leaf is everywhere and consistently delivers on
              freshness.
            </p>
            <p
              ref={(el) => setSpotRef("section-8", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.28000}
                coordX={-123.11700}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;488 Robson Street, Vancouver, BC
            </p>

            {/* SECTION: Local Regulations / Best Practices */}
            <h2 id="local-regulations">Local Regulations / Best Practices</h2>
            <p>
              All Vancouver salad bars must follow strict food safety guidelines
              set by provincial authorities, including keeping ingredients below
              4°C to prevent bacterial growth. Staff typically hold FOODSAFE
              certifications, ensuring proper handling and hygiene practices.
              Vancouver Coastal Health conducts regular inspections, and results
              are publicly available. On the sustainability front, Vancouver’s
              Single-Use Item Reduction Strategy encourages eateries to adopt
              compostable or recyclable packaging. Many of these establishments
              also prioritize local, seasonal produce, reducing their carbon
              footprint and supporting regional farms. Customers often benefit
              from clearly labeled menus highlighting allergens, dietary
              preferences, and whether ingredients are organic or farm-to-table.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Are these salad bars suitable for vegans or vegetarians?</h3>
              <p>
                Absolutely. Each spot offers plenty of plant-based options,
                including protein-rich legumes, grains, and tofu. Just let the
                staff know your preferences, or customize your own bowl for a
                completely vegan/vegetarian meal.
              </p>

              <h3>2. How much does a typical salad cost in Vancouver?</h3>
              <p>
                Expect to pay roughly $10–$15 CAD for a filling meal, though
                exact prices vary by ingredient choices and portion size. Spots
                like Green Leaf Salad Bar and Salad Loop can dip below $10, while
                more elaborate bowls at Field &amp; Social or Railtown Café may
                reach $17–$18.
              </p>

              <h3>3. Can I customize my salad?</h3>
              <p>
                Yes, customization is widely embraced. Whether you’re building
                from scratch at a self-serve bar or swapping ingredients at a
                chef-curated location, Vancouver salad bars encourage you to mix
                and match as you like.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s salad bars prove that healthy eating doesn’t have to
              be bland or boring. From Field &amp; Social’s carefully crafted
              bowls to Salad Loop’s endless customization, there’s a fresh
              option for every dietary need and craving. The city’s strict food
              safety standards and strong emphasis on sustainability mean you
              can indulge in these nutritious meals with confidence. Next time
              you’re seeking a healthy bite in Vancouver, consider one of these
              top salad bars—and experience firsthand how delicious “eating
              green” can be.
            </p>
            <p>
              <em>
                Disclaimer: Menu items, operating hours, and prices may change.
                Always check with each restaurant for the latest information or
                possible allergen concerns. 
              </em>
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay ref={mobileMapRef} markers={locations} spotRefs={spotRefs} />

      <Footer />
    </>
  );
}